import React from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { BiInfoCircle } from 'react-icons/bi';
import { BsArrowRight, BsCheckCircle } from 'react-icons/bs';

import Card from 'partial/components/Card';
import LineGraph from 'assets/images/line-graph.svg';

const MAP_DATE_BY_RANGE = {
  '4h': {
    from: dayjs().subtract(4, 'hours').format('YYYY-MM-DD HH:mm:ss'),
    to: dayjs().format('YYYY-MM-DD HH:mm:ss'),
  },
  '12h': {
    from: dayjs().subtract(12, 'hours').format('YYYY-MM-DD HH:mm:ss'),
    to: dayjs().format('YYYY-MM-DD HH:mm:ss'),
  },
  '24h': {
    from: dayjs().subtract(24, 'hours').format('YYYY-MM-DD HH:mm:ss'),
    to: dayjs().format('YYYY-MM-DD HH:mm:ss'),
  },
  '1w': {
    from: dayjs().subtract(1, 'week').format('YYYY-MM-DD HH:mm:ss'),
    to: dayjs().format('YYYY-MM-DD HH:mm:ss'),
  },
  '4w': {
    from: dayjs().subtract(4, 'weeks').format('YYYY-MM-DD HH:mm:ss'),
    to: dayjs().format('YYYY-MM-DD HH:mm:ss'),
  },
};

const useFetchData = () => {};

const Label = ({ value }) => <div className="font-bold">{value}</div>;

Label.propTypes = {
  value: PropTypes.string.isRequired,
};

const Count = ({ label, value }) => (
  <div>
    <div className="text-gray-500 flex items-center space-x-1">
      <span>{label}</span>
      <BiInfoCircle />
    </div>
    <div className="text-lg font-bold">{value}</div>
  </div>
);

Count.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const LineChart = () => (
  <div className="aspect-h-9 aspect-w-21">
    <img
      className="h-full w-full"
      // src="https://ucarecdn.com/85b239b4-407f-4569-8b8a-58e24b4b7e52/-/resize/512x/"
      src={LineGraph}
      alt=""
    />
  </div>
);

function IntegrationDashboard({ merchant }) {
  const [range] = React.useState('1D');
  // const dashboardRange = React.useMemo(
  //   () => (
  //     <div className="space-x-1 flex items-center">
  //       <div className="btn-group">
  //         <Button
  //           primary={range === '4H'}
  //           size="xs"
  //           onClick={() => setRange('4H')}
  //         >
  //           4H
  //         </Button>
  //         <Button
  //           primary={range === '12H'}
  //           size="xs"
  //           onClick={() => setRange('12H')}
  //         >
  //           12H
  //         </Button>
  //         <Button
  //           primary={range === '1D'}
  //           size="xs"
  //           onClick={() => setRange('1D')}
  //         >
  //           1D
  //         </Button>
  //         <Button
  //           primary={range === '1M'}
  //           size="xs"
  //           onClick={() => setRange('1M')}
  //         >
  //           1M
  //         </Button>
  //         <Button
  //           primary={range === '1Y'}
  //           size="xs"
  //           onClick={() => setRange('1Y')}
  //         >
  //           1Y
  //         </Button>
  //         <Button
  //           primary={range === 'ALL'}
  //           size="xs"
  //           onClick={() => setRange('ALL')}
  //         >
  //           ALL
  //         </Button>
  //         {/* <Button size="xs" icon={BiCalendar} /> */}
  //       </div>
  //     </div>
  //   ),
  //   [range, setRange]
  // );
  const filter = React.useMemo(
    () => ({
      ...MAP_DATE_BY_RANGE[range],
    }),
    [range]
  );
  useFetchData(filter);
  return (
    <div>
      <Card label="Your Integration">
        <div className="grid grid-cols-1 divide-y md:divide-y-0 md:grid-cols-3 divide-x">
          <div className="divide-y">
            <div className="p-5 space-y-2">
              <Label value="API Requests" />
              <div className="flex space-x-4">
                <Count label="Successful" value="0" />
                <Count label="Failed" value="0" />
              </div>
              <div>
                <LineChart />
              </div>
            </div>
            <div className="p-5 space-y-2">
              <Label value="Webhooks" />
              <div className="flex space-x-4">
                <Count label="Successful" value="0" />
                <Count label="Failed" value="0" />
              </div>
              <div>
                <LineChart />
              </div>
            </div>
          </div>
          <div className="divide-y">
            <div className="p-5 space-y-2">
              <Label value="API Error Distribution" />
              <div className="flex space-x-4">
                <Count label="Successful" value="0" />
                <Count label="Failed" value="0" />
              </div>
              <div>
                <LineChart />
              </div>
            </div>
            <div className="p-5 space-y-2">
              <Label value="Webhook Response Time" />
              <div className="flex space-x-4">
                <Count label="No recent webhooks attempts" value="" />
              </div>
              <div>
                <LineChart />
              </div>
            </div>
          </div>
          <div className="divide-y">
            <div className="p-5 space-y-2">
              <Label value="Integration Status" />
              <div className="text-gray-500">
                Use these test cards to test your integration.{' '}
                {merchant?.approval_status === 'APPROVED' ? null : (
                  <>
                    Once you are ready,{' '}
                    <Link
                      className="text-primary hover:underline"
                      to="/merchants/profile/kyc"
                    >
                      activate your account
                    </Link>{' '}
                    to access live API Keys
                  </>
                )}
              </div>
              <div className="flex justify-between">
                <div>Payment Gateway API Key</div>
                <div className="flex-shrink-0">
                  <span className="text-green-500 font-semibold">Success</span>
                </div>
              </div>
              <div className="flex justify-between">
                <div>Billing API Key</div>
                <div className="flex-shrink-0">
                  <span className="text-gray-500 font-semibold">
                    Not Started
                  </span>
                </div>
              </div>
              <div className="flex justify-end">
                <a
                  className="flex items-center space-x-1 font-light hover:underline"
                  href="/"
                >
                  <span>Learn about testing</span>
                  <BsArrowRight className="h-5 w-5" />
                </a>
              </div>
            </div>
            <div className="p-5 space-y-2">
              <Label value="Recent Errors" />
              <div className="p-5 flex flex-col justify-center items-center space-y-3">
                <div className="text-green-500">
                  <BsCheckCircle className="h-8 w-8" />
                </div>
                <div className="font-semibold">Everyhing looks good</div>
                <div className="font-light">
                  We found no errors in the last 7 days.
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

IntegrationDashboard.propTypes = {
  merchant: PropTypes.instanceOf(Object).isRequired,
};

export default IntegrationDashboard;
